// MessageFeed.jsx

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { formatChannelDayLabel, getChannelDayKey } from 'channels/utils/channelTime';
import { resolvePrecedingPromptText } from 'spaces/utils/aiFeedbackUtils';
import { canReplyInThreadOnMessage } from 'spaces/utils/aiThreadUtils';

// Constants
import { MESSAGE_TYPES } from 'spaces/constants/aiConstants';

// CSS
import './MessageFeed.css';

// Components
import Message from 'channels/components/Message';
import AiFeedItemMessage from 'spaces/components/AiFeedItemMessage';
import AiMessageRow from 'spaces/components/AiMessageRow';
import AiSurfaceMessage from 'spaces/components/AiSurfaceMessage';
import AiTypingIndicator from 'spaces/components/AiTypingIndicator';

const MessageFeedDayHeader = React.memo(({ label, theme }) => (

  <div className="message-feed-day-header" role="separator" aria-label={label}>

    {/* DAY LABEL */}
    <span
      className="message-feed-day-label"
    >
      {label}
    </span>
  </div>
));

MessageFeedDayHeader.displayName = 'MessageFeedDayHeader';

/**
 * MessageFeed
 *
 * Renders the channel message feed body and state rows.
 *
 * @param {array} messages - Message rows.
 * @param {boolean} loading - Loading state.
 * @param {string} errorMessage - Error state.
 * @param {string} currentUserKey - Active user key.
 * @param {object} attachmentNodesByKey - Hydrated attachment node rows by key.
 * @param {function} onOpenAttachmentNode - Called when shared attachment is opened.
 * @param {function} onEditMessage - Called when message edit is submitted.
 * @param {function} onDeleteMessage - Called when message delete is requested.
 * @param {string} scrollToMessageKey - Optional message key to scroll into view.
 * @param {boolean} hasMoreMessages - Whether messages exist beyond the loaded window.
 * @param {boolean} isLoadingOlderMessages - Whether a wider window is in flight.
 * @param {function} onLoadOlderMessages - Called to widen the loaded window.
 * @param {string} loadMoreLabel - Label for the widen affordance.
 * @param {string} loadMorePlacement - `top` for newest-first feeds, `bottom` for threads.
 *
 * @returns {ReactNode} The rendered feed.
 */
const MessageFeed = React.memo(({
  messages,
  loading,
  hasMoreMessages = false,
  isLoadingOlderMessages = false,
  onLoadOlderMessages = null,
  loadMoreLabel = 'Load earlier messages',
  loadMorePlacement = 'top',
  errorMessage,
  currentUserKey,
  attachmentNodesByKey,
  onOpenAttachmentNode,
  onEditMessage,
  onDeleteMessage,
  scrollToMessageKey = '',
  emptyPlaceholder = 'No messages yet. Be the first to send a message in this channel.',
  canReplyInThread = false,
  hasUnreadRepliesByKey = {},
  membersByKey = {},
  mentionedPeopleByKey = {},
  onOpenThread,
  onReplyInThread,
  onSelectDisambiguationOption = null,
  showAiTyping = false,
  aiPendingCount = 0,
  showMessageEditDelete = true,
  allowDeleteAnyMessage = false,
  threadSummariesByRootKey = {},
  threadRootsLimitedToFeedSurfaces = false,
  onOpenSurfaceNode = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- REFS ----- //

  const bottomRef = useRef(null);

  const containerRef = useRef(null);

  const lastMessageKeyRef = useRef('');

  const widenAnchorRef = useRef(null);

  // ----- STATE ----- //

  const [highlightMessageKey, setHighlightMessageKey] = useState('');

  // ----- EFFECTS ----- //

  // Earlier messages arrive above the viewport, so hold the reader in place by
  // absorbing the height they added instead of letting the offset drift.
  useLayoutEffect(() => {
    const anchor = widenAnchorRef.current;
    if (!anchor) return;
    widenAnchorRef.current = null;

    const container = containerRef.current;
    if (!container) return;

    const delta = container.scrollHeight - anchor.scrollHeight;
    if (delta <= 0) return;

    container.scrollTop = anchor.scrollTop + delta;
  }, [messages]);

  useEffect(() => {
    const rows = messages || [];
    const lastKey = String(rows[rows.length - 1]?.key || '');
    // Loading earlier messages grows the list at the top; jumping to the bottom
    // would throw away the position the viewer was reading.
    const grewAtBottom = lastKey !== lastMessageKeyRef.current;
    lastMessageKeyRef.current = lastKey;

    if (!bottomRef.current) return;
    if (scrollToMessageKey) return;
    if (!grewAtBottom && !showAiTyping) return;

    bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, scrollToMessageKey, showAiTyping, aiPendingCount]);

  useEffect(() => {
    const cleanTargetKey = String(scrollToMessageKey || '').trim();
    if (!cleanTargetKey || loading) return;

    const target = document.getElementById(`message-${cleanTargetKey}`);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightMessageKey(cleanTargetKey);

    const timeoutId = window.setTimeout(() => {
      setHighlightMessageKey('');
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [loading, messages, scrollToMessageKey]);

  const canLoadMore = hasMoreMessages && typeof onLoadOlderMessages === 'function';

  const handleLoadMore = () => {
    const container = containerRef.current;
    if (container && loadMorePlacement === 'top') {
      widenAnchorRef.current = {
        scrollHeight: container.scrollHeight,
        scrollTop: container.scrollTop,
      };
    }
    onLoadOlderMessages();
  };

  const loadMoreRow = canLoadMore ? (
    <div className="message-feed-load-older" key="load-more">
      <button
        type="button"
        className="message-feed-load-older-button"
        disabled={isLoadingOlderMessages}
        onClick={handleLoadMore}
      >
        {isLoadingOlderMessages ? 'Loading...' : loadMoreLabel}
      </button>
    </div>
  ) : null;

  const messageRows = [];
  let lastDayKey = null;

  (messages || []).forEach((message, index) => {
    const dayKey = getChannelDayKey(message?.dateCreated) || `unknown-${index}`;

    if (dayKey !== lastDayKey) {
      messageRows.push(
        <MessageFeedDayHeader
          key={`day-${dayKey}`}
          label={formatChannelDayLabel(message?.dateCreated)}
          theme={theme}
        />,
      );
      lastDayKey = dayKey;
    }

    const nextMessage = (messages || [])[index + 1];
    const nextDayKey = nextMessage
      ? getChannelDayKey(nextMessage?.dateCreated) || `unknown-${index + 1}`
      : null;
    const showAvatarConnector = nextDayKey === dayKey;
    const messageType = String(message?.messageType || '').trim();
    const promptText = resolvePrecedingPromptText(messages, index);

    if (messageType === MESSAGE_TYPES.AI_SURFACE) {
      messageRows.push(
        <AiSurfaceMessage
          key={message.key}
          message={message}
          promptText={promptText}
          canReplyInThread={canReplyInThreadOnMessage(message, canReplyInThread)}
          hasUnreadReplies={Boolean(hasUnreadRepliesByKey[message.key])}
          threadSummariesByRootKey={threadSummariesByRootKey}
          onOpenThread={onOpenThread}
          onReplyInThread={onReplyInThread}
        />,
      );
      return;
    }

    if (messageType === MESSAGE_TYPES.AI_FEED_ITEM) {
      messageRows.push(
        <AiFeedItemMessage
          key={message.key}
          message={message}
          promptText={promptText}
          canReplyInThread={canReplyInThreadOnMessage(message, canReplyInThread)}
          hasUnreadReplies={Boolean(hasUnreadRepliesByKey[message.key])}
          threadSummariesByRootKey={threadSummariesByRootKey}
          onOpenThread={onOpenThread}
          onReplyInThread={onReplyInThread}
        />,
      );
      return;
    }

    if (messageType === MESSAGE_TYPES.AI_TEXT) {
      const isLatestMessage = index === (messages || []).length - 1;
      messageRows.push(
        <AiMessageRow
          key={message.key}
          message={message}
          promptText={promptText}
          disambiguationEnabled={
            isLatestMessage
            && !showAiTyping
          }
          onSelectDisambiguationOption={onSelectDisambiguationOption}
          canReplyInThread={canReplyInThreadOnMessage(message, canReplyInThread)}
          hasUnreadReplies={Boolean(hasUnreadRepliesByKey[message.key])}
          threadSummariesByRootKey={threadSummariesByRootKey}
          onOpenThread={onOpenThread}
          onReplyInThread={onReplyInThread}
          onOpenCitation={onOpenSurfaceNode}
        />,
      );
      return;
    }

    messageRows.push(
      <Message
        key={message.key}
        message={message}
        isOwn={currentUserKey === message?.userKey}
        attachmentNodesByKey={attachmentNodesByKey}
        showAvatarConnector={showAvatarConnector}
        isHighlighted={highlightMessageKey === message.key}
        canReplyInThread={
          threadRootsLimitedToFeedSurfaces
            ? canReplyInThreadOnMessage(message, canReplyInThread)
            : canReplyInThread
        }
        hasUnreadReplies={Boolean(hasUnreadRepliesByKey[message.key])}
        membersByKey={membersByKey}
        mentionedPeopleByKey={mentionedPeopleByKey}
        allowEditDelete={showMessageEditDelete}
        allowDeleteAny={allowDeleteAnyMessage}
        threadSummariesByRootKey={threadSummariesByRootKey}
        onDelete={onDeleteMessage}
        onEdit={onEditMessage}
        onOpenAttachmentNode={onOpenAttachmentNode}
        onOpenThread={onOpenThread}
        onReplyInThread={onReplyInThread}
      />,
    );
  });

  // ----- RENDER ----- //

  return (
    <div className="message-feed-container" ref={containerRef}>

      {/* STATES */}
      {loading ? (

        <div
          className="message-feed-placeholder"
        >
          Loading messages...
        </div>
      ) : null}

      {/* ERROR */}
      {!loading && errorMessage ? (

        <div className="message-feed-error">{errorMessage}</div>
      ) : null}

      {/* STATES */}
      {!loading && !errorMessage && (messages || []).length === 0 ? (

        <div
          className="message-feed-placeholder"
        >
          {emptyPlaceholder}
        </div>
      ) : null}

      {!loading && !errorMessage ? (

        <div className="message-feed-list">

          {/* SPACER */}
          <div className="message-feed-spacer" aria-hidden />

          {loadMorePlacement === 'top' ? loadMoreRow : null}
          {messageRows}
          {loadMorePlacement === 'bottom' ? loadMoreRow : null}
          {showAiTyping ? (
            <AiTypingIndicator pendingCount={aiPendingCount} />
          ) : null}
          <div ref={bottomRef} />
        </div>
      ) : null}
    </div>
  );
});

// Dev Tools
MessageFeed.displayName = 'MessageFeed';

export default MessageFeed;
